import {
  DataStatus,
  FormField,
  Form,
  FormSubmitHandler,
  ButtonGroup,
  FormButton,
  Separator,
} from 'platform/components';
import {Grid, Heading, Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import {
  useGetServiceOrderIntegrationClaraInformationQuery,
  usePatchServiceOrderIntegrationClaraInformationMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

interface OrderClaraInformationProps extends TestIdProps {
  orderId: string;
  serviceCaseId: string;
  isReadOnly?: boolean;
}

export function OrderClaraInformation(props: OrderClaraInformationProps) {
  const {
    data: serviceOrderClaraInformation,
    isError: isServiceOrderClaraInformationError,
    isLoading: isServiceOrderClaraInformationLoading,
  } = useGetServiceOrderIntegrationClaraInformationQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.orderId,
  });
  const [patchServiceOrderIntegrationClaraInformation] =
    usePatchServiceOrderIntegrationClaraInformationMutation();

  const handleSubmit: FormSubmitHandler<FormType> = async (data) =>
    await patchServiceOrderIntegrationClaraInformation({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.orderId,
      body: {damageCode: data.damageCode ?? null},
    })
      .unwrap()
      .catch(handleApiError);

  return (
    <DataStatus
      isLoading={isServiceOrderClaraInformationLoading}
      isError={isServiceOrderClaraInformationError}
    >
      <VStack spacing={1}>
        <Heading size={4}>{i18n.t('entity.serviceOrder.labels.claraIntegration')}</Heading>
        <Text size="xSmall" color="secondary">
          {i18n.t('entity.serviceOrder.labels.claraIntegrationDescription')}
        </Text>
      </VStack>
      <Separator />
      <Form<FormType>
        onSubmit={handleSubmit}
        experimentalZodSchema={formSchema}
        defaultValues={{damageCode: serviceOrderClaraInformation?.damageCode}}
      >
        {(control) => (
          <VStack spacing={4}>
            <Grid columns={4} spacing={4}>
              <FormField
                control={control}
                name="damageCode"
                type="text"
                label={i18n.t('entity.serviceOrder.labels.damageCode')}
                maxLength={7}
                isCounterVisible
                isDisabled={props.isReadOnly}
                data-testid={suffixTestId('damageCode', props)}
              />
            </Grid>
            <ButtonGroup align="right">
              <FormButton
                control={control}
                type="submit"
                title={i18n.t('general.actions.saveChanges')}
                isDisabled={props.isReadOnly}
                data-testid={suffixTestId('save', props)}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}

const formSchema = z.object({
  damageCode: z.string().max(7).nullish(),
});

type FormType = z.infer<typeof formSchema>;
